/* eslint-disable no-shadow */
import {
    call, takeEvery
} from "redux-saga/effects";
import { ONE_SECOND } from "Globals/constants/constants";

import {
    CHANGE_MULTIPLE_STATUS_REQUESTED,
    CHANGE_STATUS_REQUESTED,
    CREATE_REQUESTED,
    LOAD_ASSIGNABLE_TO_WORKFLOW_REQUESTED,
    LOAD_REQUESTED,
    LOAD_SINGLE_REQUESTED,
    UPDATE_REQUESTED,
    defaultScopeId,
    failChangeUserStatus,
    failChangeUsersStatus,
    failCreateUser,
    failLoadUser,
    failLoadUsers,
    failLoadUsersAssignableToWorkflow,
    failUpdateUser,
    successChangeUserStatus,
    successChangeUsersStatus,
    successCreateUser,
    successLoadUser,
    successLoadUsers,
    successLoadUsersAssignableToWorkflow,
    successUpdateUser
} from "./users.duck";

import {
    changeUserStatus,
    changeUsersStatus,
    createUser,
    loadAllUsers,
    loadSingleUser,
    loadUserAssignableToWorkflow,
    updateUser
} from "./users.api";
import noop from "lodash.noop";

import {
    getId, injectApiWithContextFactory, injectBoundActionCreatorsFactory, takeFirstAndLatest, withRefreshRequest 
} from "Utils/sagaUtils";
import { injectBoundGQLClient } from "Utils/injectBoundGQLClient";

export const withBoundActions = injectBoundActionCreatorsFactory(
    {
        failChangeUserStatus,
        failChangeUsersStatus,
        failCreateUser,
        failLoadUser,
        failLoadUsers,
        failLoadUsersAssignableToWorkflow,
        failUpdateUser,
        successChangeUserStatus,
        successChangeUsersStatus,
        successCreateUser,
        successLoadUser,
        successLoadUsers,
        successLoadUsersAssignableToWorkflow,
        successUpdateUser
    },
    getId,
    defaultScopeId
);

export const withApiContext = injectApiWithContextFactory(
    {
        changeUserStatus,
        changeUsersStatus,
        createUser,
        loadAllUsers,
        loadSingleUser,
        loadUserAssignableToWorkflow,
        updateUser
    },
    injectBoundGQLClient
);

export function* loadUsersAssignableToWorkflowSaga({
    data,
    actions: {
        successLoadUsersAssignableToWorkflow,
        failLoadUsersAssignableToWorkflow,
    },
    api: {
        loadUserAssignableToWorkflow = noop
    },
    resolve = noop,
    reject = noop,
}) {
    try {
        const result = yield loadUserAssignableToWorkflow(data);
        yield successLoadUsersAssignableToWorkflow(result);
        yield call(resolve);
    } catch (e) {
        yield failLoadUsersAssignableToWorkflow(e);
        yield call(reject, e);
    }
}

export function* loadUsersSaga({
    actions: { successLoadUsers = noop, failLoadUsers = noop },
    api: { loadAllUsers = noop },
    resolve = noop,
    reject = noop,
}) {
    try {
        const { result } = yield loadAllUsers();
        yield successLoadUsers(result);
        yield call(resolve);
    } catch (e) {
        yield failLoadUsers(e);
        yield call(reject, e);
    }
}

export function* loadSingleUserSaga({
    data, api: { loadSingleUser = noop }, actions: { successLoadUser = noop, failLoadUser = noop }, resolve = noop, reject = noop 
}){
    try {
        const { result } = yield loadSingleUser({ id: data });
        yield successLoadUser(result);
        yield call(resolve);
    } catch (e){
        yield failLoadUser(e);
        yield call(reject, e);
    }
}

export function* createUserSaga({
    data: { user: { role, ...newUser }, roleOptions },
    api: { createUser = noop },
    actions: { successCreateUser = noop, failCreateUser = noop },
    resolve = noop,
    reject = noop,
}) {
    try {
        const roleGroup = roleOptions?.find(({ name }) => name === role);
        if (!roleGroup) throw new Error("Could not find group for selected role");
        const passive = roleGroup?.name === "REVIEWER";
        const { user } = yield createUser({ newUser, role: roleGroup?.id, passive });
        const userwithRoleGroup = user?.groups.find(({ id }) => id === roleGroup?.id) ? user : { ...user, groups: [roleGroup]};
        yield successCreateUser(userwithRoleGroup);
        yield call(resolve);
    } catch (e) {
        yield failCreateUser(e);
        yield call(reject, e);
    }
}

export function* changeUserStatusSaga({
    data: { userId, currentGroupId, targetGroup },
    actions: { successChangeUserStatus = noop, failChangeUserStatus = noop },
    api: { changeUserStatus = noop },
    resolve = noop,
    reject = noop,
}) {
    try {
        yield changeUserStatus({
            userId,
            addUserGroups: [targetGroup?.id],
            removeUserGroups: [currentGroupId]
        });
        yield successChangeUserStatus({ userId, group: targetGroup });
        yield call(resolve);
    } catch (e) {
        yield failChangeUserStatus(e);
        yield call(reject, e);
    }
}

export function* changeUsersStateSaga({
    data: { userData, targetGroups },
    actions: {
        successChangeUsersStatus = noop,
        failChangeUsersStatus = noop,
    },
    api: { changeUsersStatus = noop },
    resolve = noop,
    reject = noop,
}) {
    const users = userData.filter(({ currentRole }) => currentRole).map(({ userId, currentRole }) => {
        const targetGroup = targetGroups.find(({ name }) => name.split("-")[0] === (currentRole.name.split("-")[0]));
        return {
            userId,
            addUserGroups: [targetGroup?.id],
            removeUserGroups: [currentRole?.id],
            passive: targetGroup?.name?.endsWith("-DISABLED")
        };
    })
        .filter(({ addUserGroups, removeUserGroups }) => addUserGroups[0] !== removeUserGroups[0]);
    try {
        if (users.length > 0){
            yield changeUsersStatus({ usersToRemove: users });
            const userResult = users.map(
                ({ userId, addUserGroups: [gid] }) => ({
                    userId,
                    group: targetGroups.find(
                        ({ id }) => id === gid
                    ),
                })
            );
            yield successChangeUsersStatus(userResult);
        }
        yield call(resolve);
    } catch (e) {
        yield failChangeUsersStatus(e);
        yield call(reject, e);
    }
}

export function* updateUserSaga({
    data: {
        initialRole,
        role,
        projectGroups,
        groups,
        ...userData
    },
    actions: {
        successUpdateUser = noop,
        failUpdateUser = noop,
    },
    api: { 
        updateUser = noop, 
        changeUserStatus = noop 
    },
    resolve = noop,
    reject = noop,
}) {
    try {
        if (role !== initialRole){
            const groupToRemove = groups.find(({ name }) => name === initialRole);
            const groupToAdd = projectGroups.find(({ name }) => name === role);
            yield changeUserStatus({
                userId: userData.id, 
                addUserGroups: [groupToAdd?.id], 
                removeUserGroups: [groupToRemove?.id], 
                passive: role === "REVIEWER"
            });
        }
        const { result } = yield updateUser(userData);
        yield successUpdateUser(result);
        yield call(resolve);
    } catch (e) {
        yield failUpdateUser(e);
        yield call(reject, e);
    }
}

export default () => function* rootSaga() {
    yield takeEvery(LOAD_ASSIGNABLE_TO_WORKFLOW_REQUESTED, withRefreshRequest(withApiContext(withBoundActions(loadUsersAssignableToWorkflowSaga))));
    yield takeFirstAndLatest(
        LOAD_REQUESTED,
        withRefreshRequest(withBoundActions(withApiContext(loadUsersSaga))),
        ONE_SECOND / 3,
    );
    yield takeEvery(LOAD_SINGLE_REQUESTED, withRefreshRequest(withApiContext(withBoundActions(loadSingleUserSaga))));
    yield takeEvery(CREATE_REQUESTED, withApiContext(withBoundActions(createUserSaga)));
    yield takeEvery(CHANGE_MULTIPLE_STATUS_REQUESTED, withApiContext(withBoundActions(changeUsersStateSaga)));
    yield takeEvery(CHANGE_STATUS_REQUESTED, withApiContext(withBoundActions(changeUserStatusSaga)));
    yield takeEvery(UPDATE_REQUESTED, withApiContext(withBoundActions(updateUserSaga)));
};
